import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Input, Select } from 'antd';
import { SendOutlined, RocketOutlined, FolderOutlined, EditOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { renderMarkdownSafely } from '../../pages/DashboardUtils';
import apiClient from '../../services/api';

const { TextArea } = Input;

const fontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * BrainstormSessionPanel - The active brainstorming chat session
 *
 * Shown after the task definition phase is complete.
 * Provides a chat interface with library selection and web search toggle.
 */
const BrainstormSessionPanel = ({ onRedefine, selectedNote, taskDefinition }) => {
  const { t } = useTranslation(['dashboard', 'messages', 'common']);
  const contentRef = useRef(null);
  const prevNoteIdRef = useRef(null);
  const historyLoadedRef = useRef(false);
  const configLoadedRef = useRef(false);

  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLibraries, setSelectedLibraries] = useState([]);
  const [privateTopics, setPrivateTopics] = useState([]);
  const [privateKBInfo, setPrivateKBInfo] = useState(null);
  const [collaborativeKBs, setCollaborativeKBs] = useState([]);
  const [collaborativeKBTopics, setCollaborativeKBTopics] = useState({});
  const [topicNotesCount, setTopicNotesCount] = useState({});
  const [dataReady, setDataReady] = useState(false);

  // Collect all topic IDs from a topic tree
  const collectTopicIds = (topics) => {
    const ids = [];
    const walk = (list) => {
      if (!list) return;
      list.forEach(topic => {
        ids.push(topic.topic_id);
        if (topic.children) walk(topic.children);
      });
    };
    walk(topics);
    return ids;
  };

  // Fetch knowledge bases, topic trees, and notes count on mount
  useEffect(() => {
    const fetchData = async () => {
      let allPrivateTopics = [];
      let allCollabTopicsMap = {};

      try {
        const privateRes = await apiClient.get('/users/knowledge-base/private/topics');
        const privateData = privateRes.data?.data || privateRes.data;
        if (privateData?.knowledgeBase) {
          setPrivateKBInfo(privateData.knowledgeBase);
          allPrivateTopics = privateData.topics || [];
          setPrivateTopics(allPrivateTopics);
        }
      } catch (error) {
        // No private KB
      }

      try {
        const collabRes = await apiClient.get('/users/knowledge-base/collaborative');
        const collabKBs = collabRes.data?.data || [];
        setCollaborativeKBs(collabKBs);

        await Promise.all(collabKBs.map(async (kb) => {
          try {
            const topicsRes = await apiClient.get(`/users/knowledge-base/collaborative/${kb.kb_id}/topics`);
            allCollabTopicsMap[kb.kb_id] = topicsRes.data?.data || [];
          } catch (e) {
            allCollabTopicsMap[kb.kb_id] = [];
          }
        }));
        setCollaborativeKBTopics(allCollabTopicsMap);
      } catch (error) {
        // No collaborative KBs
      }

      // Fetch notes count for all topics
      const allTopicIds = [
        ...collectTopicIds(allPrivateTopics),
        ...Object.values(allCollabTopicsMap).flatMap(topics => collectTopicIds(topics))
      ];
      if (allTopicIds.length > 0) {
        try {
          const countRes = await apiClient.post('/notes/topics/count', { topicIds: allTopicIds });
          setTopicNotesCount(countRes.data || {});
        } catch (error) {
          // Ignore count errors
        }
      }
      setDataReady(true);
    };
    fetchData();
  }, []);

  // Build flat options as "Library - Topic - Subtopic", filtering out topics with zero notes
  const libraryOptions = useMemo(() => {
    const isInboxTopic = (name) => {
      const lower = (name || '').toLowerCase();
      return lower === 'my_inbox' || lower === 'team_inbox';
    };

    const flattenTopics = (topics) => {
      if (!topics || topics.length === 0) return [];
      const result = [];
      topics.forEach(topic => {
        if (isInboxTopic(topic.name)) return;
        const count = topicNotesCount[topic.topic_id] || 0;
        const childOptions = flattenTopics(topic.children);
        const hasChildren = topic.children && topic.children.length > 0;
        if (!hasChildren && count > 0) {
          result.push({
            label: topic.name,
            value: `topic:${topic.topic_id}`
          });
        }
        result.push(...childOptions);
      });
      return result;
    };

    const options = [];

    if (privateKBInfo) {
      options.push(...flattenTopics(privateTopics));
    }

    collaborativeKBs.forEach(kb => {
      options.push({
        label: `[Team] ${kb.name}`,
        value: `kb:${kb.kb_id}`
      });
    });

    return options;
  }, [privateKBInfo, privateTopics, collaborativeKBs, collaborativeKBTopics, topicNotesCount, t]);

  // Load saved library selection from brainstorm_config, fallback to note's topics
  useEffect(() => {
    if (!selectedNote?.id || !dataReady || libraryOptions.length === 0 || configLoadedRef.current) return;

    const loadConfig = async () => {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        const response = await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=brainstorm_config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const saved = data.messages?.[0]?.selectedLibraries;
          if (Array.isArray(saved)) {
            // Respect saved config even if empty (user intentionally cleared libraries)
            const availableValues = new Set(libraryOptions.map(opt => opt.value));
            const valid = saved.filter(v => availableValues.has(v));
            setSelectedLibraries(valid);
            configLoadedRef.current = true;
            return;
          }
        }
      } catch (error) {
        // Ignore load errors
      }

      // Fallback: pre-select from note's topic mappings
      configLoadedRef.current = true;
      if (selectedLibraries.length > 0) return;
      const noteTopicIds = selectedNote?.topicMappings?.map(m => m.topic_id) || [];
      if (noteTopicIds.length === 0) return;
      const availableValues = new Set(libraryOptions.map(opt => opt.value));
      const matches = noteTopicIds
        .map(id => `topic:${id}`)
        .filter(v => availableValues.has(v));
      if (matches.length > 0) {
        setSelectedLibraries(matches);
      }
    };

    loadConfig();
  }, [selectedNote?.id, dataReady, libraryOptions]);

  // Save library selection to brainstorm_config when it changes
  useEffect(() => {
    if (!selectedNote?.id || !configLoadedRef.current) return;

    const saveConfig = async () => {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            type: 'brainstorm_config',
            messages: [{ selectedLibraries }]
          })
        });
      } catch (error) {
        console.error('[BrainstormSessionPanel] Failed to save library config:', error);
      }
    };

    const timeoutId = setTimeout(saveConfig, 500);
    return () => clearTimeout(timeoutId);
  }, [selectedLibraries, selectedNote?.id]);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversation history when note changes
  useEffect(() => {
    const loadConversationHistory = async () => {
      if (!selectedNote?.id) return;
      if (prevNoteIdRef.current === selectedNote.id) return;
      prevNoteIdRef.current = selectedNote.id;

      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        const response = await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}?type=brainstorm`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            const converted = data.messages.map(msg => ({
              id: msg.id?.toString() || Date.now().toString(),
              role: msg.type === 'user' ? 'user' : 'assistant',
              content: msg.content
            }));
            setMessages(converted);
          }
        }
        historyLoadedRef.current = true;
      } catch (error) {
        console.error('[BrainstormSessionPanel] Failed to load conversation history:', error);
        historyLoadedRef.current = true;
      }
    };

    loadConversationHistory();
  }, [selectedNote?.id]);

  // Save conversation history when it changes
  useEffect(() => {
    const saveChatHistory = async () => {
      if (!selectedNote?.id || !historyLoadedRef.current) return;
      if (messages.length === 0) return;

      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;
      if (!token) return;

      try {
        const converted = messages.map(msg => ({
          id: parseInt(msg.id) || Date.now(),
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content,
          timestamp: new Date().toLocaleTimeString()
        }));

        await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ type: 'brainstorm', messages: converted })
        });
      } catch (error) {
        console.error('[BrainstormSessionPanel] Failed to save chat history:', error);
      }
    };

    const timeoutId = setTimeout(saveChatHistory, 1000);
    return () => clearTimeout(timeoutId);
  }, [messages, selectedNote?.id]);

  const handleSubmit = async () => {
    if (!input.trim() || isLoading) return;

    const currentMessage = input.trim();
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: currentMessage
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Build QA history from past messages
    const historyMessages = messages;
    const recentHistory = historyMessages.slice(-10);
    const qaHistoryPairs = [];
    for (let i = 0; i < recentHistory.length - 1; i += 2) {
      const userMsg = recentHistory[i];
      const assistantMsg = recentHistory[i + 1];
      if (userMsg?.role === 'user' && assistantMsg?.role === 'assistant') {
        qaHistoryPairs.push({ question: userMsg.content, answer: assistantMsg.content });
      }
    }

    const aiMessageId = (Date.now() + 1).toString();

    // Add AI placeholder with streaming indicator
    setMessages(prev => [...prev, {
      id: aiMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true
    }]);

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '/api';
      const authStorage = localStorage.getItem('auth-storage');
      const token = authStorage ? JSON.parse(authStorage).state?.token : null;

      const response = await fetch(`${baseUrl}/notes/qa/brainstorm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          noteId: selectedNote.id,
          message: currentMessage,
          taskDefinition,
          libraries: selectedLibraries,
          qaHistory: qaHistoryPairs
        })
      });

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error('INSUFFICIENT_CREDITS');
        }
        throw new Error('SERVICE_TEMPORARILY_UNAVAILABLE');
      }

      const data = await response.json();
      const answerContent = data.response || data.answer || data.message || '';
      const fallbackMessage = t('dashboard:brainstorm_no_answer', {
        defaultValue: "I don't have enough information to answer this. Please clarify the question or feel free to ask another one."
      });
      setMessages(prev => prev.map(msg => {
        if (msg.id === aiMessageId) {
          return {
            ...msg,
            content: answerContent.trim() ? answerContent : fallbackMessage,
            isStreaming: false
          };
        }
        return msg;
      }));
    } catch (error) {
      console.error('[BrainstormSessionPanel] Brainstorm failed:', error);
      const errorMessage = error.message === 'INSUFFICIENT_CREDITS'
        ? t('dashboard:insufficient_credits', { defaultValue: 'Insufficient credits. Please purchase more credits to continue.' })
        : t('messages:qa_service_unavailable', { defaultValue: 'Sorry, the service is temporarily unavailable. Please try again later.' });
      setMessages(prev => prev.map(msg => {
        if (msg.id === aiMessageId) {
          return {
            ...msg,
            content: errorMessage,
            isStreaming: false
          };
        }
        return msg;
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = async () => {
    if (isLoading) return;
    setMessages([]);
    // Clear brainstorm messages in DB (set to empty array)
    const baseUrl = process.env.REACT_APP_API_URL || '/api';
    const authStorage = localStorage.getItem('auth-storage');
    const token = authStorage ? JSON.parse(authStorage).state?.token : null;
    if (token && selectedNote?.id) {
      try {
        await fetch(`${baseUrl}/notes/conversations/${selectedNote.id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ type: 'brainstorm', messages: [] })
        });
      } catch (error) {
        console.error('[BrainstormSessionPanel] Failed to clear conversation:', error);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fafafa' }}>
      <style>{`
        .brainstorm-message-content p {
          margin: 0;
          font-size: 14px;
          line-height: 22px;
          font-weight: 400;
        }
        .brainstorm-message-content p + p {
          margin-top: 8px;
        }
        .brainstorm-message-content ul, .brainstorm-message-content ol {
          margin: 8px 0;
          padding-left: 20px;
        }
        .brainstorm-message-content li {
          font-size: 14px;
          line-height: 22px;
          font-weight: 400;
        }
        @keyframes brainstormBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '16px 24px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <RocketOutlined style={{ fontSize: 16, color: '#fff' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827', lineHeight: '20px', fontFamily, WebkitFontSmoothing: 'antialiased' }}>
              {t('dashboard:ai_brainstorm', { defaultValue: 'Ideas Brainstorm' })} - {t('dashboard:brainstorm_stage_brainstorm', { defaultValue: 'Brainstorming' })}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: '#4b5563', lineHeight: '16px', fontFamily, WebkitFontSmoothing: 'antialiased', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('dashboard:ai_brainstorm_desc', { defaultValue: 'Expand ideas with AI knowledge and your own knowledge hub' })}
            </p>
          </div>
          <button
            onClick={handleNewChat}
            disabled={isLoading || messages.length === 0}
            title={t('dashboard:brainstorm_clear_history', { defaultValue: 'Clear History' })}
            style={{
              background: messages.length === 0 || isLoading ? '#e5e7eb' : '#2563eb',
              border: 'none',
              cursor: messages.length === 0 || isLoading ? 'default' : 'pointer',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 6,
              color: messages.length === 0 || isLoading ? '#9ca3af' : '#fff',
              fontSize: 12,
              fontFamily
            }}
            onMouseEnter={(e) => { if (messages.length > 0 && !isLoading) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
            onMouseLeave={(e) => { if (messages.length > 0 && !isLoading) e.currentTarget.style.backgroundColor = '#2563eb'; else e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
          >
            <ClearOutlined style={{ fontSize: 13 }} />
            {t('dashboard:brainstorm_clear_history', { defaultValue: 'Clear History' })}
          </button>
        </div>
      </div>

      {/* Chat Content Area */}
      <div
        ref={contentRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}
      >
        {/* Task definition card */}
        {taskDefinition?.summary && (
          <div style={{
            backgroundColor: '#eff6ff',
            border: '1px solid #93c5fd',
            borderRadius: 12,
            padding: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h4 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#1e40af', fontFamily, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('dashboard:brainstorm_task_definition', { defaultValue: 'Task Definition' })}
              </h4>
              <button
                onClick={onRedefine}
                style={{
                  background: 'none',
                  border: '1px solid #93c5fd',
                  borderRadius: 6,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#2563eb',
                  fontFamily,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#dbeafe'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <EditOutlined style={{ fontSize: 11 }} />
                {t('dashboard:brainstorm_redefine', { defaultValue: 'Redefine' })}
              </button>
            </div>
            <p style={{ margin: 0, fontSize: 14, lineHeight: '22px', color: '#111827', fontFamily, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {taskDefinition.summary}
            </p>
          </div>
        )}

        {/* Chat messages */}
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            {msg.role === 'assistant' && (
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <RocketOutlined style={{ fontSize: 16, color: '#2563eb' }} />
              </div>
            )}
            <div style={{
              flex: 1,
              borderRadius: 16,
              padding: '12px 16px',
              backgroundColor: msg.role === 'user' ? '#dbeafe' : '#fff',
              border: msg.role === 'assistant' ? '1px solid #e5e7eb' : 'none',
              color: '#111827'
            }}>
              {msg.isStreaming && !msg.content ? (
                <div style={{ display: 'flex', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '0ms' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '150ms' }} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#9ca3af', animation: 'brainstormBounce 1s infinite', animationDelay: '300ms' }} />
                </div>
              ) : msg.role === 'assistant' ? (
                <div
                  className="brainstorm-message-content"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdownSafely(msg.content)
                  }}
                  style={{
                    fontSize: 14,
                    lineHeight: '22px',
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily,
                    WebkitFontSmoothing: 'antialiased'
                  }}
                />
              ) : (
                <p style={{
                  margin: 0,
                  fontSize: 14,
                  lineHeight: '22px',
                  fontFamily,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {msg.content}
                </p>
              )}
            </div>
            {msg.role === 'user' && (
              <div style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 500,
                color: '#374151'
              }}>
                {t('common:you_short', { defaultValue: 'You' })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input Area */}
      <div style={{
        borderTop: '1px solid #e5e7eb',
        padding: 16
      }}>
        <div style={{
          border: '1px solid #d1d5db',
          borderRadius: 10,
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}>
          {/* Chat input */}
          <div style={{ padding: '12px 12px 8px 12px' }}>
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t('dashboard:brainstorm_chat_placeholder', { defaultValue: 'Ask a question or explore an idea...' })}
              autoSize={{ minRows: 1, maxRows: 5 }}
              style={{
                padding: '4px 4px',
                fontSize: 14,
                lineHeight: '20px',
                resize: 'none',
                border: 'none',
                fontFamily,
                boxShadow: 'none'
              }}
            />
          </div>

          {/* Bottom bar: Library selector + Web search + Send button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            borderTop: '1px solid #f3f4f6',
            backgroundColor: '#fafafa'
          }}>
            {/* Library selector */}
            <FolderOutlined style={{ fontSize: 14, color: '#9ca3af', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                mode="multiple"
                allowClear
                placeholder={t('dashboard:brainstorm_select_libraries', { defaultValue: 'Select libraries for context...' })}
                value={selectedLibraries}
                onChange={setSelectedLibraries}
                options={libraryOptions}
                style={{ width: '100%' }}
                size="small"
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                variant="borderless"
              />
            </div>

            {/* Send button */}
            <button
              onClick={handleSubmit}
              disabled={!input.trim() || isLoading}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: 'none',
                backgroundColor: input.trim() && !isLoading ? '#2563eb' : '#e5e7eb',
                color: input.trim() && !isLoading ? '#fff' : '#9ca3af',
                cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                flexShrink: 0
              }}
            >
              <SendOutlined style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrainstormSessionPanel;
